import {selectModeData} from "../../redux/selectors/editor";
import {IGraphData, UpdateModeData, updateModeData} from "../../redux/slices/editor";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import {useNodes, useReactFlow, useViewport} from "react-flow-renderer";
import {getFontEmbedCSS, toPng} from "html-to-image";
import {saveAs} from "file-saver";
import {selectNetwork} from "../../redux/selectors/firebase";
import {NetworkData} from "../../fstn/network";

const border = 50;

interface IGraphExporter {
    graph: HTMLDivElement | null
    graphData: IGraphData
    network: NetworkData
    updateModeData: UpdateModeData
}
function GraphExporter({graph, graphData, network, updateModeData}: IGraphExporter) {
    const [dimensions, setDimensions] = useState({height: 0, width: 0});
    const [offset, setOffset] = useState({top: 0, left: 0});
    const [pendingExport, setPendingExport] = useState(false);
    const nodes = useNodes();
    const {setViewport} = useReactFlow();
    const {x, y, zoom} = useViewport();
    const handleExportGraph = useCallback(() => {
        setViewport({
            x: border - offset.left,
            y: border - offset.top,
            zoom: 1
        });
        setPendingExport(true);
    }, [offset, setPendingExport, setViewport]);

    useEffect(() => {
        const bounds = nodes.reduce<{minX: number, maxX: number, minY: number, maxY: number}>(
            (previous, node) => {
                return {
                    minX: Math.min(previous.minX, node.position.x),
                    maxX: Math.max(previous.maxX, node.position.x + (node.width || 0)),
                    minY: Math.min(previous.minY, node.position.y),
                    maxY: Math.max(previous.maxY, node.position.y + (node.height || 0))
                }
            }, {
                minX: Number.MAX_VALUE,
                maxX: Number.MIN_VALUE,
                minY: Number.MAX_VALUE,
                maxY: Number.MIN_VALUE
            });
        const height = bounds.maxY - bounds.minY;
        const width = bounds.maxX - bounds.minX

        if (dimensions.height !== height || dimensions.width !== width) {
            setDimensions({height, width});
        }
        if (bounds.minY !== offset.top || bounds.minX !== offset.left) {
            setOffset({top: bounds.minY, left: bounds.minX});
        } 
    }, [dimensions, offset, nodes, setDimensions, setOffset]);
    useEffect(() => {
        if (graphData.events.includes("exportGraph")) {
            handleExportGraph();
            updateModeData({events: graphData.events.filter(event => event !== "exportGraph")})
        }
    }, [graphData, handleExportGraph, updateModeData]);
    useEffect(() => {
        if (pendingExport && x === border - offset.left && y === border - offset.top && zoom === 1 && graph) {
            setPendingExport(false);
            getFontEmbedCSS(graph).then(fontEmbeddedCSS => toPng(graph, {
                backgroundColor: "lightGrey",
                fontEmbedCSS: fontEmbeddedCSS,
                skipAutoScale: true,
                height: dimensions.height + border * 2,
                width: dimensions.width + border * 2
            })).then(dataUrl => {
                const root = network.states[graphData.root];

                saveAs(dataUrl, `${root?.name || "graph"}.png`);
            });
        }
    }, [dimensions, graph, graphData.root, network.states, offset, pendingExport, setPendingExport, x, y, zoom])
    return null;
}
const mapStateToProps = (store: never) => ({
    graphData: selectModeData(store) as IGraphData,
    network: selectNetwork(store)
});
export default connect(mapStateToProps, {
    updateModeData,
})(GraphExporter);
